// Guests Also Considered — anonymized, aggregated recommendations.
//
// Powered by `/api/hotels/recommendations`. Falls back internally to the
// Similar-Hotels rail (same city / price / amenities) when aggregated
// signals are too thin — so the section always renders when there ARE
// matching hotels in the destination.
//
// Excludes the current hotel + everything in the caller's local
// "Recently viewed" list (so we never re-suggest what they've already
// seen). No individual user data leaves the browser — only hotel ids
// go to the server.

import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Sparkles, Loader2, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';
import { useCurrency } from '../../context/CurrencyContext';
import { getRecentHotels } from '../../lib/recent';
import NoImagePlaceholder from './NoImagePlaceholder';

const GuestsAlsoConsidered = ({ hotel, searchCtx, nights }) => {
  const { formatPrice } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [fallback, setFallback] = useState(false);

  const canFetch = !!(hotel?.id && hotel?.city && searchCtx?.check_in && searchCtx?.check_out);

  const excludeIds = useMemo(
    () => getRecentHotels().map((h) => String(h.id)),
    // Rebuild when the target hotel changes so we always include the fresh viewer history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hotel?.id],
  );

  useEffect(() => {
    if (!canFetch) { setHidden(true); return; }
    let cancelled = false;
    setLoading(true);
    setHidden(false);
    api.post('/hotels/recommendations', {
      hotel_id: hotel.id,
      city: hotel.city,
      check_in: searchCtx.check_in,
      check_out: searchCtx.check_out,
      rooms: searchCtx.rooms || 1,
      guests: searchCtx.guests || 2,
      nights: nights || 1,
      lat: hotel.coordinates?.latitude,
      lng: hotel.coordinates?.longitude,
      price: hotel.net_price_per_night ?? hotel.display_price ?? hotel.price,
      star_rating: Number(hotel.category) || null,
      amenities: hotel.amenities || [],
      provider: hotel.provider,
      exclude_hotel_ids: excludeIds,
      tier: 'b2c',
    })
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data?.hotels || []);
        setFallback(!!data?.fallback_mode);
      })
      .catch(() => { if (!cancelled) setHidden(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [
    canFetch, hotel?.id, hotel?.city, hotel?.provider,
    searchCtx?.check_in, searchCtx?.check_out, searchCtx?.rooms, searchCtx?.guests,
    nights, hotel?.coordinates?.latitude, hotel?.coordinates?.longitude,
    hotel?.net_price_per_night, hotel?.display_price, hotel?.price,
    hotel?.category, hotel?.amenities, excludeIds,
  ]);

  if (hidden || (!loading && items.length === 0)) return null;

  const linkFor = (h) => {
    const params = new URLSearchParams();
    if (searchCtx.city)      params.set('city', searchCtx.city);
    if (searchCtx.check_in)  params.set('check_in', searchCtx.check_in);
    if (searchCtx.check_out) params.set('check_out', searchCtx.check_out);
    if (searchCtx.rooms)     params.set('rooms', String(searchCtx.rooms));
    if (searchCtx.guests)    params.set('guests', String(searchCtx.guests));
    return `/hotels/${h.id}?${params.toString()}`;
  };

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8"
      data-testid="guests-also-considered"
      data-fallback={fallback ? 'yes' : 'no'}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest font-semibold text-red-600 dark:text-red-400 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Personalised for you
          </p>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Guests Also Considered</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {fallback
              ? 'Similar properties in this destination.'
              : 'Anonymized picks based on what other guests explored alongside this stay.'}
          </p>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Curating recommendations…
        </div>
      ) : (
        <div
          className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 sm:mx-0 px-4 sm:px-0"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {items.map((h) => {
            const stars = Math.max(0, Math.min(5, Math.round(Number(h.rating) || 0)));
            const img = (h.images && h.images[0]) || h.image;
            const googleRating = h.google_rating;
            return (
              <div
                key={h.id}
                data-testid={`gac-hotel-${h.id}`}
                className="group relative min-w-[240px] sm:min-w-[260px] max-w-[260px] shrink-0 rounded-xl overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all hover:-translate-y-0.5"
                style={{ scrollSnapAlign: 'start' }}
              >
                <Link to={linkFor(h)} className="block">
                  <div className="h-36 bg-gray-100 dark:bg-gray-800 overflow-hidden relative">
                    {img ? (
                      <img
                        src={img}
                        alt={h.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <NoImagePlaceholder />
                    )}
                    {!fallback && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-black/60 backdrop-blur text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                        <Sparkles className="h-2.5 w-2.5" /> Popular pick
                      </span>
                    )}
                  </div>
                  <div className="p-3 space-y-1">
                    <div className="flex items-start gap-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate flex-1">{h.name}</p>
                      {(googleRating || h.rating) > 0 && (
                        <span className="shrink-0 inline-flex items-center gap-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold px-1.5 py-0.5 rounded">
                          {Number(googleRating || h.rating).toFixed(1)} <Star className="h-2.5 w-2.5 fill-current" />
                        </span>
                      )}
                    </div>
                    {stars > 0 && (
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: stars }).map((_, i) => (
                          <Star key={i} className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
                      <MapPin className="h-3 w-3 flex-shrink-0" />
                      {[h.area, h.city].filter(Boolean).join(', ') || '—'}
                    </p>
                    <p className="text-sm font-semibold text-red-600 dark:text-red-400 pt-1">
                      {formatPrice(h.display_price)}
                      <span className="text-[10px] text-gray-500 ml-1 font-normal">total ({nights}n)</span>
                    </p>
                  </div>
                </Link>
                <div className="px-3 pb-3">
                  <Link
                    to={linkFor(h)}
                    data-testid={`gac-hotel-cta-${h.id}`}
                    className="w-full inline-flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-md bg-red-600 hover:bg-red-700 text-white transition"
                  >
                    View Rooms <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default GuestsAlsoConsidered;
